import { React, useState, useEffect, useCallback } from "react";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers";
import {
  Grid,
  Typography,
  useTheme,
  useMediaQuery,
  Select,
  MenuItem,
  FormControl,
  TextField,
  Box
} from "@mui/material";
import { useTranslation } from 'react-i18next';
import CopyButton from '../common/CopyButton';
import {
  CalculatorContainer,
  CalculatorTitle,
  DateContainer,
  ResultTypography,
  datePickerStyles,
  textFieldStyles,
  selectStyles
} from '../../shared/styles';

function RecurringDateCalculator() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState(dayjs());
  const [recurrenceType, setRecurrenceType] = useState('weekly');
  const [recurrenceValue, setRecurrenceValue] = useState(1);
  const [numberOfOccurrences, setNumberOfOccurrences] = useState(5);
  const [resultDates, setResultDates] = useState([]);

  const calculateRecurringDates = useCallback(() => {
    const dates = [];
    let currentDate = startDate;

    for (let i = 0; i < numberOfOccurrences; i++) {
      dates.push(currentDate);

      switch (recurrenceType) {
        case 'daily':
          currentDate = currentDate.add(recurrenceValue, 'day');
          break;
        case 'weekly':
          currentDate = currentDate.add(recurrenceValue, 'week');
          break;
        case 'monthly':
          currentDate = currentDate.add(recurrenceValue, 'month');
          break;
        case 'yearly':
          currentDate = currentDate.add(recurrenceValue, 'year');
          break;
        default:
          break;
      }
    }

    return dates;
  }, [startDate, recurrenceType, recurrenceValue, numberOfOccurrences]);

  useEffect(() => {
    setResultDates(calculateRecurringDates());
  }, [calculateRecurringDates]);

  const resultText = resultDates.map(date => date.format('YYYY-MM-DD')).join('\n');

  return (
    <CalculatorContainer>
      <CalculatorTitle variant={isMobile ? "h5" : "h4"}>
        {t('recurringDateCalc.title')}
      </CalculatorTitle>
      <Grid
        container
        spacing={isMobile ? 2 : 3}
        justifyContent="center"
        sx={{ flexGrow: 1 }}
      >
        <Grid item xs={12} sm={6}>
          <DateContainer>
            <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom>
              {t('recurringDateCalc.startDate')}
            </Typography>
            <DatePicker
              onChange={setStartDate}
              value={startDate}
              sx={datePickerStyles}
            />
          </DateContainer>
        </Grid>
        <Grid item xs={12} sm={6}>
          <DateContainer>
            <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom>
              Recurrence Type
            </Typography>
            <FormControl fullWidth>
              <Select
                value={recurrenceType}
                onChange={(e) => setRecurrenceType(e.target.value)}
                sx={selectStyles}
              >
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
              </Select>
            </FormControl>
          </DateContainer>
        </Grid>
        <Grid item xs={12} sm={6}>
          <DateContainer>
            <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom>
              {t('recurringDateCalc.intervalDays')}
            </Typography>
            <TextField
              type="number"
              value={recurrenceValue}
              onChange={(e) => setRecurrenceValue(Number(e.target.value))}
              fullWidth
              sx={textFieldStyles}
              inputProps={{ min: 1 }}
            />
          </DateContainer>
        </Grid>
        <Grid item xs={12} sm={6}>
          <DateContainer>
            <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom>
              {t('recurringDateCalc.occurrences')}
            </Typography>
            <TextField
              type="number"
              value={numberOfOccurrences}
              onChange={(e) => setNumberOfOccurrences(Number(e.target.value))}
              fullWidth
              sx={textFieldStyles}
              inputProps={{ min: 1, max: 100 }}
            />
          </DateContainer>
        </Grid>
      </Grid>
      <ResultTypography
        variant={isMobile ? "h6" : "h5"}
        sx={{
          mt: 'auto',
          mb: 2,
          padding: 2,
          maxHeight: '280px',
          overflow: 'hidden',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          backgroundColor: 'rgba(187, 222, 251, 0.2)',
          borderRadius: 2,
        }}
      >
        <Box sx={{
          width: '100%',
          flex: 1,
          overflow: 'auto',
          pr: 2,
          pt: 1,
          pb: 1,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(145px, 1fr))',
          gap: 1.5,
          alignContent: 'start',
          '&::-webkit-scrollbar': {
            width: '6px',
            height: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(187, 222, 251, 0.1)',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(144, 202, 249, 0.6)',
            borderRadius: '3px',
            '&:hover': {
              background: 'rgba(144, 202, 249, 0.8)',
            },
          },
          '&::-webkit-scrollbar-corner': {
            background: 'rgba(187, 222, 251, 0.1)',
          },
        }}>
          {resultDates.map((date, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 1,
                backgroundColor: '#ffffff',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
                color: 'text.primary',
                fontSize: '0.9rem',
                border: '1px solid rgba(144, 202, 249, 0.3)',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.08)',
                  borderColor: 'rgba(144, 202, 249, 0.8)',
                }
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  mr: 1.5,
                  color: 'primary.main',
                  fontWeight: 600,
                  backgroundColor: 'rgba(144, 202, 249, 0.15)',
                  px: 0.8,
                  py: 0.2,
                  borderRadius: '4px'
                }}
              >
                #{index + 1}
              </Typography>
              {date.format('YYYY-MM-DD')}
            </Box>
          ))}
        </Box>
        <Box sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 1,
        }}>
          <CopyButton text={resultText} />
        </Box>
      </ResultTypography>
    </CalculatorContainer>
  );
}

export default RecurringDateCalculator; 